import { useCallback, useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactFlow, {
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Controls,
  ReactFlowProvider,
  getIncomers,
  getOutgoers,
  getConnectedEdges,
  useReactFlow,
} from "reactflow";
import { userSlice } from "../redux/user/userSlice";
import { Button, CircularProgress } from "@mui/material";
import { NodeWithToolbar } from "./nodes/NodeWithToolBar";
import { FirstSceneNode } from "./nodes/FirstSceneNode";
import ButtonNode from "./nodes/ButtonNode";
import GroupNode from "./nodes/GroupNode";
import "reactflow/dist/style.css";
import "./index.css";
import { useLocation } from "react-router-dom";
import Settings from "./Settings";
import ScenarioAnalytics from "./ScenarioAnalytics";
import { saveGame } from "../redux/asyncThunk/userActions";

import {
  getGroupStyle,
  getTrainerGroupStyle,
  getSceneStyle,
  isFirstSceneNode,
  isProtectedSchemeNode,
  normalizeSchemeNodes,
  SCHEME_SCENE,
  SCHEME_GROUP,
  TRAINER_SCHEME_GROUP,
  stripSchemeTransient,
  FIRST_SCENE_ID,
} from "./nodes/schemeLayout";
import NodeDeleteContext from "./nodes/NodeDeleteContext";
import { isTrainerGame } from "../utils/contentType";

const { showDialog, addMessage } = userSlice;

const nodeTypes = {
  "first-scene-node": FirstSceneNode,
  "node-with-toolbar": NodeWithToolbar,
  input: ButtonNode,
  group: GroupNode,
};

const SCENE_NODE_TYPES = new Set(["node-with-toolbar", "first-scene-node"]);

function isButtonToSceneConnection(sourceNode, targetNode) {
  if (sourceNode?.type !== "input") return false;
  return SCENE_NODE_TYPES.has(targetNode?.type);
}

function upsertButtonEdge(currentEdges, connection) {
  const withoutSource = currentEdges.filter(
    (edge) => edge.source !== connection.source
  );

  return [
    ...withoutSource,
    {
      id: connection.source,
      source: connection.source,
      target: connection.target,
      type: "step",
    },
  ];
}

function dedupeButtonEdges(edgeList) {
  if (!edgeList?.length) return edgeList ?? [];

  const seenSources = new Set();
  return edgeList.filter((edge) => {
    if (edge?.target === FIRST_SCENE_ID) {
      return false;
    }

    if (!edge?.source || seenSources.has(edge.source)) {
      return false;
    }
    seenSources.add(edge.source);
    return true;
  });
}

function buildSceneFromButton(sourceId, currentNodes, currentEdges, game, position) {
  const sourceNode = currentNodes.find((node) => node.id === sourceId);

  if (sourceNode?.type !== "input") {
    return null;
  }

  if (currentEdges.some((edge) => edge.source === sourceId)) {
    return null;
  }

  const newId = Date.now().toString();
  const groupId = `${newId}-group`;
  const isTrainer = isTrainerGame(game);
  const parentGroup = currentNodes.find((node) => node.id === sourceNode.parentId);
  const groupWidth = isTrainer ? TRAINER_SCHEME_GROUP.width : SCHEME_GROUP.width;
  const scenePosition =
    position ??
    {
      x: (parentGroup?.position?.x ?? 0) + groupWidth + 80,
      y: parentGroup?.position?.y ?? 0,
    };

  const groupNode = {
    id: groupId,
    type: "group",
    className: "scheme-group",
    data: { label: isTrainer ? `Вопрос ${newId}` : `Сцена ${newId}` },
    dragHandle: ".scheme-group-drag-handle",
    position: scenePosition,
    style: isTrainer ? getTrainerGroupStyle() : getGroupStyle(),
  };

  const newNode = {
    id: newId,
    data: {
      label: isTrainer ? `Вопрос ${newId}` : `Сцена ${newId}`,
      questionMode: "video",
      questionText: "",
    },
    position: { x: SCHEME_SCENE.x, y: SCHEME_SCENE.y },
    type: "node-with-toolbar",
    targetPosition: "left",
    className: "scheme-scene",
    style: getSceneStyle(),
    extent: "parent",
    parentNode: groupId,
    parentId: groupId,
    draggable: false,
  };

  const newEdge = {
    id: sourceId,
    source: sourceId,
    target: newId,
    type: "step",
  };

  return {
    nextNodes: [...currentNodes, groupNode, newNode],
    nextEdges: [...currentEdges, newEdge],
  };
}

const Flow = (props) => {
  const { game } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const isLoading = useSelector((state) => state.user?.status);
  const reactFlowWrapper = useRef(null);
  const connectingNodeId = useRef(null);
  const { screenToFlowPosition, getNodes, getEdges } = useReactFlow();

  const { type } = location.state || {};

  const [viewMode, setViewMode] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    game?.nodes ? game?.nodes : null
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    game?.edges ? game?.edges : null
  );

  useEffect(() => {
    if (type === "settings") {
      setViewMode(type);
    } else {
      setViewMode("scheme");
    }
  }, [type]);

  const isTrainerProject = isTrainerGame(game);

  useEffect(() => {
    if (!game?._id) return;

    const serverEdges = dedupeButtonEdges(game.edges ?? []);

    setNodes(
      normalizeSchemeNodes(game.nodes)?.map((node) =>
        node.type === "group" && isTrainerProject
          ? { ...node, style: { ...node.style, ...getTrainerGroupStyle() } }
          : node
      )
    );

    setEdges((currentEdges) => {
      if (serverEdges.length > 0) {
        return serverEdges;
      }
      if (currentEdges?.length > 0) {
        return currentEdges;
      }
      return serverEdges;
    });
  }, [
    game?._id,
    game?.updatedAt,
    game?.nodes,
    game?.edges,
    isTrainerProject,
    setEdges,
    setNodes,
  ]);

  const onConnect = useCallback(
    (connection) => {
      const currentNodes = getNodes();
      const sourceNode = currentNodes.find((node) => node.id === connection.source);
      const targetNode = currentNodes.find((node) => node.id === connection.target);

      if (!isButtonToSceneConnection(sourceNode, targetNode)) {
        return;
      }

      setEdges((currentEdges) => {
        const hasConnection = currentEdges.some(
          (edge) => edge.source === connection.source
        );

        if (hasConnection) {
          dispatch(
            addMessage({
              type: "error",
              message: "Кнопка может вести только к одной сцене",
            })
          );
          return currentEdges;
        }

        const nextEdges = upsertButtonEdge(currentEdges, connection);

        dispatch(
          saveGame({
            selectedGameId: game?._id,
            nodes: stripSchemeTransient(currentNodes),
            edges: nextEdges,
          })
        );

        return nextEdges;
      });
    },
    [dispatch, game?._id, getNodes, setEdges]
  );

  const isValidConnection = useCallback(
    (connection) => {
      const currentNodes = getNodes();
      const currentEdges = getEdges();
      const sourceNode = currentNodes.find((node) => node.id === connection.source);
      const targetNode = currentNodes.find((node) => node.id === connection.target);

      if (!isButtonToSceneConnection(sourceNode, targetNode)) {
        return false;
      }

      if (isFirstSceneNode(targetNode)) {
        return false;
      }

      return !currentEdges.some((edge) => edge.source === connection.source);
    },
    [getEdges, getNodes]
  );

  const onConnectStart = useCallback((_, { nodeId }) => {
    connectingNodeId.current = nodeId;
  }, []);

  const onConnectEnd = useCallback(
    (event) => {
      const sourceId = connectingNodeId.current;
      connectingNodeId.current = null;

      if (!sourceId) return;

      const targetIsPane = event.target.classList.contains("react-flow__pane");

      if (!targetIsPane) return;

      const currentEdges = getEdges();
      const currentNodes = getNodes();

      if (currentEdges.some((edge) => edge.source === sourceId)) {
        dispatch(
          addMessage({
            type: "error",
            message:
              "Кнопка уже связана со сценой. Удалите текущую связь, чтобы создать новую",
          })
        );
        return;
      }

      const { clientX, clientY } = event;
      const position = screenToFlowPosition({ x: clientX, y: clientY });
      const created = buildSceneFromButton(
        sourceId,
        currentNodes,
        currentEdges,
        game,
        position
      );

      if (!created) return;

      const { nextNodes, nextEdges } = created;

      setNodes(nextNodes);
      setEdges(nextEdges);

      dispatch(
        saveGame({
          selectedGameId: game?._id,
          nodes: stripSchemeTransient(nextNodes),
          edges: nextEdges,
        })
      );
    },
    [
      dispatch,
      game,
      getEdges,
      getNodes,
      screenToFlowPosition,
      setEdges,
      setNodes,
    ]
  );

  const onCreateSceneFromButton = useCallback(
    (sourceId) => {
      const currentEdges = getEdges();
      const currentNodes = getNodes();

      if (currentEdges.some((edge) => edge.source === sourceId)) {
        dispatch(
          addMessage({
            type: "error",
            message:
              "Кнопка уже связана со сценой. Удалите текущую связь, чтобы создать новую",
          })
        );
        return;
      }

      const created = buildSceneFromButton(
        sourceId,
        currentNodes,
        currentEdges,
        game
      );

      if (!created) return;

      const { nextNodes, nextEdges } = created;

      setNodes(nextNodes);
      setEdges(nextEdges);

      dispatch(
        saveGame({
          selectedGameId: game?._id,
          nodes: stripSchemeTransient(nextNodes),
          edges: nextEdges,
        })
      );
    },
    [dispatch, game, getEdges, getNodes, setEdges, setNodes]
  );

  const onNodesDelete = useCallback(
    (deleted) => {
      if (!game?.nodes) return;

      const deletableNodes = deleted.filter((node) => !isProtectedSchemeNode(node));
      if (!deletableNodes.length) return;

      let startRemainingEdges = [];
      let updateNodes = [...game.nodes];

      setEdges(
        deletableNodes.reduce((acc, node) => {
          const incomers = getIncomers(node, nodes, edges);
          const outgoers = getOutgoers(node, nodes, edges);
          const connectedEdges = getConnectedEdges([node], edges);

          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge)
          );

          const createdEdges = incomers.flatMap(({ id: source }) =>
            outgoers.map(({ id: target }) => ({
              id: `${source}->${target}`,
              source,
              target,
            }))
          );

          const temp = updateNodes.filter((el) => el.id !== node.id);

          updateNodes = temp;
          startRemainingEdges = [...remainingEdges, ...createdEdges];
          return [...remainingEdges, ...createdEdges];
        }, edges)
      );

      dispatch(
        saveGame({
          selectedGameId: game._id,
          nodes: stripSchemeTransient(updateNodes),
          edges: startRemainingEdges,
        })
      );
    },
    [dispatch, edges, game._id, game.nodes, nodes, setEdges]
  );

  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes }) => {
      const sceneSelectedGroupIds = new Set();
      const elevatedGroupIds = new Set();

      selectedNodes.forEach((node) => {
        if (node.type === "group") {
          sceneSelectedGroupIds.add(node.id);
          elevatedGroupIds.add(node.id);
        }

        if (
          node.type === "node-with-toolbar" ||
          node.type === "first-scene-node"
        ) {
          if (node.parentId) {
            sceneSelectedGroupIds.add(node.parentId);
            elevatedGroupIds.add(node.parentId);
          }
        }

        if (node.type === "input" && node.parentId) {
          elevatedGroupIds.add(node.parentId);
        }
      });

      setNodes((nds) =>
        nds.map((node) => {
          if (node.type !== "group") return node;

          const isSceneSelected = sceneSelectedGroupIds.has(node.id);
          const isElevated = elevatedGroupIds.has(node.id);

          return {
            ...node,
            className: isElevated
              ? "scheme-group scheme-group--active"
              : "scheme-group",
            style: { ...node.style, zIndex: isElevated ? 1000 : 1 },
            data: { ...node.data, isSceneSelected },
          };
        })
      );
    },
    [setNodes]
  );

  const onNodeDrag = useCallback(
    (_event, node) => {
      if (node.type !== "group") return;

      setNodes((nds) =>
        nds.map((item) => {
          if (item.type !== "group") return item;

          const isActive = item.id === node.id;
          return {
            ...item,
            className: isActive
              ? "scheme-group scheme-group--active"
              : "scheme-group",
            style: { ...item.style, zIndex: isActive ? 1000 : 1 },
          };
        })
      );
    },
    [setNodes]
  );

  const onNodeDragStart = useCallback(
    (_event, node) => {
      if (node.type !== "group") return;

      setNodes((nds) =>
        nds.map((item) => {
          if (item.type !== "group") return item;

          const isActive = item.id === node.id;
          return {
            ...item,
            className: isActive
              ? "scheme-group scheme-group--active"
              : "scheme-group",
            style: { ...item.style, zIndex: isActive ? 1000 : 1 },
          };
        })
      );
    },
    [setNodes]
  );

  const onNodeDragStop = useCallback(
    (_event, node) => {
      if (node.type !== "group" || !game?._id) return;

      dispatch(
        saveGame({
          selectedGameId: game._id,
          nodes: stripSchemeTransient(getNodes()),
          edges: getEdges(),
        })
      );
    },
    [dispatch, game?._id, getEdges, getNodes]
  );

  const switchComp = (str) => {
    switch (str) {
      case "settings":
        return <Settings />;
      case "analytics":
        return <ScenarioAnalytics />;
      case "scheme":
        return (
          <NodeDeleteContext.Provider
            value={{ onNodesDelete, nodes, edges, onCreateSceneFromButton }}
          >
            <div className="wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodesDelete={onNodesDelete}
                onConnect={onConnect}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                isValidConnection={isValidConnection}
                onSelectionChange={onSelectionChange}
                onNodeDrag={onNodeDrag}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                fitView={false}
                className="react-flow-subflows-example"
                nodeTypes={nodeTypes}
                defaultEdgeOptions={{
                  type: "step",
                  style: { stroke: "#4a7fad", strokeWidth: 2 },
                }}
              >
                <Controls showInteractive={false} />
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={20}
                  size={1}
                  color="rgba(48, 84, 120, 0.18)"
                />
              </ReactFlow>
            </div>
          </NodeDeleteContext.Provider>
        );
      default:
        return;
    }
  };

  return (
    <div className="app-editor__content">
      <div className="app-editor__toolbar">
        <Button
          variant="outlined"
          size="small"
          className={`app-editor__tab${
            viewMode === "scheme" ? " app-editor__tab--active" : ""
          }`}
          onClick={() => setViewMode("scheme")}
        >
          Схема проекта
        </Button>

        <Button
          variant="outlined"
          size="small"
          className={`app-editor__tab${
            viewMode === "settings" ? " app-editor__tab--active" : ""
          }`}
          onClick={() => setViewMode("settings")}
        >
          Настройки проекта
        </Button>

        <Button
          variant="outlined"
          size="small"
          className={`app-editor__tab${
            viewMode === "analytics" ? " app-editor__tab--active" : ""
          }`}
          onClick={() => setViewMode("analytics")}
        >
          Аналитика
        </Button>

        <Button
          variant="contained"
          onClick={() => dispatch(showDialog("delete"))}
          color="error"
          size="small"
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
        >
          Удалить
        </Button>
      </div>

      {isLoading ? (
        <div className="app-loading">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div
            className="app-editor__canvas"
            hidden={viewMode !== "scheme"}
            aria-hidden={viewMode !== "scheme"}
          >
            {switchComp("scheme")}
          </div>
          {viewMode !== "scheme" ? (
            <div className="app-editor__panel">{switchComp(viewMode)}</div>
          ) : null}
        </>
      )}
    </div>
  );
};

function Workarea(props) {
  const { game } = props;

  return (
    <ReactFlowProvider>
      <Flow {...props} game={game} />
    </ReactFlowProvider>
  );
}

export default Workarea;
